"use client";

import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Check, GraduationCap, Cake, User } from "lucide-react";
import axiosInstance from "@/service/axiosInstance";
import { addVivaItem } from "@/redux/features/vivaCartSlice";
import { mapToVivaCartItem } from "@/utils/utils";
import MediaGallery from "@/components/ListingDetails/listing-details-1/MediaGallery";
import ShareButton from "@/components/ui/ShareButton";
import type { CSSProperties } from "react";
import { filterItems } from "@/redux/features/filterSlice";

/* ─── helpers ──────────────────────────────────────────────────────────────── */

const fmtGrade = (name: string) =>
  /grade/i.test(name) ? name : `Grade ${name}`;

const fmtDate = (d: string) => {
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

const fmtPrice = (n: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);

/* ─── props ─────────────────────────────────────────────────────────────────── */

interface Props {
  firstEventData: any;
  groupName: string;
  subType?: string;
  participationType?: string;
  category?: string;
  isPrivate?: boolean;
  searchParams: any;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   Main component — full-width single-column layout
   Image → Title → Form (venue, batch) → Register → About → Rules
═══════════════════════════════════════════════════════════════════════════════ */

export default function EventDetailClient({
  firstEventData,
  groupName,
  subType,
  participationType,
  category,
  isPrivate = false,
  searchParams,
}: Props) {
  const dispatch = useDispatch();
  const router = useRouter();
  const filter = useSelector(filterItems);

  const [allEvents, setAllEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);

  /* venue carried over from the /events filter (via ?venue=…), used to
     auto-select the Venue dropdown once the options have loaded. */
  const [preselectVenue, setPreselectVenue] = useState<string | null>(null);

  /* wizard selections */
  const [selVenue, setSelVenue] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [selSchool, setSelSchool] = useState<{
    name: string;
    id: number;
  } | null>(null);
  const [selGender, setSelGender] = useState<string | null>(null);
  const [selAgeGrade, setSelAgeGrade] = useState<{
    label: string;
    value: string;
    type: "age" | "grade";
  } | null>(null);
  const [selWeight, setSelWeight] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selEvents, setSelEvents] = useState<any[]>([]);

  /* ── fetch ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!groupName) return;
    axiosInstance
      .post("/events/v3/filtered-events", {
        group_name: groupName,
        ...(participationType ? { participation_type: participationType } : {}),
        ...(subType ? { eventsType: subType } : {}),
        ...(category ? { category } : {}),
        limit: 1000,
        isPrivate,
        registrationsOpen: true,
        age: searchParams.age && Number(searchParams.age),
        grade: searchParams.grade && Number(searchParams.grade),
        school: searchParams.venue,
        location: filter.location?.school_parent_ids,
      })
      .then((r: any) => {
        setAllEvents(r?.events || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [groupName]);

  /* ── step 1: venues ─────────────────────────────────────────────────────── */
  const venueOptions = useMemo(() => {
    const unique = [
      ...new Set(
        allEvents.map((e) => e.attributes?.venue_name).filter(Boolean),
      ),
    ].sort() as string[];
    return unique.map((v) => ({ value: v, label: v }));
  }, [allEvents]);

  const eventsAtVenue = useMemo(
    () =>
      selVenue
        ? allEvents.filter((e) => e.attributes?.venue_name === selVenue.value)
        : allEvents,
    [allEvents, selVenue],
  );

  /* Read the venue carried from the /events filter (?venue=…) on mount. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const v = new URLSearchParams(window.location.search).get("venue");
    if (v) setPreselectVenue(v);
  }, []);

  /* Auto-select that venue once the options are available. Falls back to a
     manual pick if the carried venue doesn't offer this event. */
  useEffect(() => {
    if (selVenue || !preselectVenue || venueOptions.length === 0) return;
    const match = venueOptions.find((o) => o.value === preselectVenue);
    if (match) setSelVenue(match);
  }, [venueOptions, preselectVenue, selVenue]);

  /* ── step 2: schools ─────────────────────────────────────────────────────── */
  const schools = useMemo(() => {
    if (!selVenue) return [];
    const m = new Map<number, { name: string; id: number }>();
    for (const e of eventsAtVenue) {
      const sd = e.attributes?.school?.data;
      if (sd?.id && sd?.attributes?.name)
        m.set(sd.id, { name: sd.attributes.name, id: sd.id });
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [eventsAtVenue, selVenue]);

  useEffect(() => {
    setSelSchool(null);
    setSelGender(null);
    setSelAgeGrade(null);
    setSelEvents([]);
  }, [selVenue]);

  useEffect(() => {
    if (schools.length === 1) setSelSchool(schools[0]);
  }, [schools]);

  const schoolResolved =
    selVenue !== null && (schools.length === 0 || selSchool !== null);

  const eventsAtSchool = useMemo(
    () =>
      selSchool
        ? eventsAtVenue.filter(
            (e) => e.attributes?.school?.data?.id === selSchool.id,
          )
        : eventsAtVenue,
    [eventsAtVenue, selSchool],
  );

  /* ── step 3: genders ────────────────────────────────────────────────────── */
  const genders = useMemo(() => {
    const all = eventsAtSchool
      .map((e) => e.attributes?.gender)
      .filter(Boolean) as string[];
    const nonAll = [...new Set(all)].filter((g) => g.toLowerCase() !== "all");
    const allSet = [...new Set(all)];
    return nonAll.length > 0 ? nonAll.sort() : allSet.length > 0 ? ["All"] : [];
  }, [eventsAtSchool]);

  useEffect(() => {
    setSelGender(null);
    setSelWeight(null);
    setSelAgeGrade(null);
    setSelEvents([]);
  }, [selSchool]);

  useEffect(() => {
    if (schoolResolved && genders.length === 1) setSelGender(genders[0]);
  }, [genders, schoolResolved]);

  const genderResolved =
    schoolResolved && (genders.length === 0 || selGender !== null);

  const eventsAfterGender = useMemo(() => {
    if (!selGender || selGender === "All") return eventsAtSchool;
    return eventsAtSchool.filter((e) => {
      const g = (e.attributes?.gender || "").toLowerCase();
      return g === "all" || g === selGender.toLowerCase();
    });
  }, [eventsAtSchool, selGender]);

  const eventsAfterAgeGrade = useMemo(() => {
    if (!selAgeGrade) return eventsAfterGender;

    const [, p1, p2] = selAgeGrade.value.split(":");

    return eventsAfterGender.filter((e) => {
      const attrs = e.attributes;

      if (selAgeGrade.type === "grade") {
        const gf = Number(
          attrs?.Grade_From?.data?.attributes?.Calculation_Value,
        );
        const gt = Number(attrs?.Grade_To?.data?.attributes?.Calculation_Value);

        return gf === Number(p1) && gt === Number(p2);
      }

      // Age
      const af = p1 ? Number(p1) : null;
      const at = p2 ? Number(p2) : null;

      return (
        (af === null
          ? attrs?.age_from == null
          : Number(attrs?.age_from) === af) &&
        (at === null ? attrs?.age_to == null : Number(attrs?.age_to) === at)
      );
    });
  }, [eventsAfterGender, selAgeGrade]);

  /* ── step 4: age / grade ────────────────────────────────────────────────── */
  const ageGradeOptions = useMemo(() => {
    type Opt = {
      label: string;
      value: string;
      type: "age" | "grade";
      sort: number;
    };
    const seen = new Set<string>();
    const opts: Opt[] = [];
    for (const e of eventsAfterGender) {
      const attrs = e.attributes;
      const gf = attrs?.Grade_From?.data?.attributes;
      const gt = attrs?.Grade_To?.data?.attributes;
      if (gf?.Calculation_Value != null && gt?.Calculation_Value != null) {
        const key = `grade:${gf.Calculation_Value}:${gt.Calculation_Value}`;
        if (!seen.has(key)) {
          seen.add(key);
          const fromName = fmtGrade(
            gf.short_name || gf.name || String(gf.Calculation_Value),
          );
          const toStr = (
            gt.short_name ||
            gt.name ||
            String(gt.Calculation_Value)
          ).replace(/grade\s*/i, "");
          const label =
            String(gf.Calculation_Value) === String(gt.Calculation_Value)
              ? fromName
              : `${fromName} – ${toStr}`;
          opts.push({
            label,
            value: key,
            type: "grade",
            sort: Number(gf.Calculation_Value),
          });
        }
      }
      const af = attrs?.age_from;
      const at = attrs?.age_to;
      if (af != null || at != null) {
        const key = `age:${af ?? ""}:${at ?? ""}`;
        if (!seen.has(key)) {
          seen.add(key);
          const label =
            af != null && at != null
              ? af > 1
                ? `${af} - ${at} yrs`
                : `Under ${at} yrs`
              : at != null
                ? `${at} yrs`
                : `${af} yrs & Above`;
          opts.push({ label, value: key, type: "age", sort: Number(at ?? 0) });
        }
      }
    }
    return opts.sort((a, b) => a.sort - b.sort);
  }, [eventsAfterGender]);

  useEffect(() => {
    setSelAgeGrade(null);
    setSelWeight(null);
    setSelEvents([]);
  }, [selGender]);

  useEffect(() => {
    if (genderResolved && ageGradeOptions.length === 1)
      setSelAgeGrade(ageGradeOptions[0]);
  }, [ageGradeOptions, genderResolved]);

  const ageGradeResolved =
    genderResolved && (ageGradeOptions.length === 0 || selAgeGrade !== null);

  /* ── matching events / batches ───────────────────────────────────────────── */
  const matchingEvents = useMemo(() => {
    let filtered = eventsAfterGender;

    // Age / Grade filter
    if (selAgeGrade) {
      const [, p1, p2] = selAgeGrade.value.split(":");

      if (selAgeGrade.type === "grade") {
        const fv = Number(p1);
        const tv = Number(p2);

        filtered = filtered.filter((e) => {
          const gf = Number(
            e.attributes?.Grade_From?.data?.attributes?.Calculation_Value,
          );
          const gt = Number(
            e.attributes?.Grade_To?.data?.attributes?.Calculation_Value,
          );
          return gf === fv && gt === tv;
        });
      } else {
        const af = p1 ? Number(p1) : null;
        const at = p2 ? Number(p2) : null;

        filtered = filtered.filter((e) => {
          const eAf = e.attributes?.age_from;
          const eAt = e.attributes?.age_to;

          return (
            (af === null ? eAf == null : Number(eAf) === af) &&
            (at === null ? eAt == null : Number(eAt) === at)
          );
        });
      }
    }

    // Weight filter
    if (selWeight) {
      const [wf, wt] = selWeight.value.split("-").map(Number);

      filtered = filtered.filter((e) => {
        return (
          Number(e.attributes?.weight_from) === wf &&
          Number(e.attributes?.weight_to) === wt
        );
      });
    }

    return filtered;
  }, [eventsAfterGender, selAgeGrade, selWeight]);

  const weightOptions = useMemo(() => {
    if (!selAgeGrade) return [];

    const seen = new Set<string>();

    return eventsAfterAgeGrade
      .filter((e) => {
        const a = e.attributes;
        return a?.weight_from != null && a?.weight_to != null;
      })
      .map((e) => {
        const a = e.attributes;
        const key = `${a.weight_from}-${a.weight_to}`;

        if (seen.has(key)) return null;
        seen.add(key);

        const data = {
          label:
            a.weight_from === a.weight_to
              ? `${a.weight_from} kg`
              : `${a.weight_from} - ${a.weight_to} kg`,
          value: key,
          sort: Number(a.weight_from),
        };
        return data;
      })
      .filter(Boolean)
      .sort((a: any, b: any) => a.sort - b.sort);
  }, [eventsAfterGender, selAgeGrade]);

  useEffect(() => {
    if (!ageGradeResolved) return;
    setSelEvents((prev) => {
      // Keep only the currently-selected events that still match the filters…
      const stillValid = prev.filter((e) =>
        matchingEvents.some((m) => m.id === e.id),
      );
      // …and auto-select when there is exactly one option and nothing chosen.
      if (stillValid.length === 0 && matchingEvents.length === 1) {
        return [matchingEvents[0]];
      }
      return stillValid;
    });
  }, [matchingEvents, ageGradeResolved]);

  const eventResolved = ageGradeResolved && selEvents.length > 0;

  useEffect(() => {
    if (!selAgeGrade) return;

    if (weightOptions.length === 1 && !selWeight) {
      setSelWeight(weightOptions[0]);
    }
  }, [selAgeGrade, weightOptions, selWeight]);

  /* ── register action ─────────────────────────────────────────────────────── */
  const handleRegister = () => {
    if (isRegistering) return;

    setIsRegistering(true);

    if (selEvents.length === 0) return;
    const token =
      typeof window !== "undefined" ? localStorage.getItem("token") : null;
    const [, agePart1, agePart2] = (selAgeGrade?.value || "::").split(":");
    const carriedAge =
      selAgeGrade?.type === "age" && agePart2 ? parseInt(agePart2) : 0;
    const carriedGrade =
      selAgeGrade?.type === "grade" && agePart1 && agePart2
        ? `${agePart1}_${agePart2}`
        : 0;
    const carriedWeight = selWeight;
    // Add every selected event to the cart. Each becomes its own cart item.
    selEvents.forEach((ev) => {
      const enriched = {
        ...ev,
        attributes: {
          ...ev.attributes,
          host_school_name: selSchool?.name || ev.attributes?.venue_name || "",
          venue_name: selVenue?.value || ev.attributes?.venue_name || "",
          gender: selGender || ev.attributes?.gender || "",
          age: carriedAge,
          grade: carriedGrade,
          SelectedEvent: ev,
          ...(carriedWeight && { weight: carriedWeight?.value }),
        },
      };
      // Persist the selection to the cart BEFORE the auth check so it survives
      // the login redirect (the user continues to /cart with events added).
      dispatch(
        addVivaItem({ data: mapToVivaCartItem(enriched), persist: true }),
      );
    });
    if (!token) {
      router.push("/login?redirect=true");
      return;
    }
    router.push("/cart");
  };

  /* ── visibility flags ────────────────────────────────────────────────────── */
  const showSchoolDropdown = selVenue !== null && schools.length > 1;
  const showBatchSection = schoolResolved;
  const showGenderStep = showBatchSection && genders.length > 0;
  const showAgeGradeStep = showBatchSection && ageGradeOptions.length > 0;
  const showWeightStep = showBatchSection && weightOptions.length > 0;
  const weightResolved = showWeightStep && weightOptions.length > 0;
  const showBatchCards =
    ageGradeResolved &&
    (!showWeightStep || selWeight !== null) &&
    matchingEvents.length > 0;
  /* ── event header data ────────────────────────────────────────────────────── */
  const subTypeName =
    firstEventData?.attributes?.fees_sub_type?.data?.attributes?.name;

  /* ─────────────────────────────────────────────────────────────────────────
     Render — single column, full-width
  ───────────────────────────────────────────────────────────────────────── */
  return (
    <div className="edc-page">
      {/* ── 1. Full-width gallery / banner ── */}
      {!isPrivate && <MediaGallery data={firstEventData} />}

      {/* ── 2. Title row ── */}
      <div className="edc-title-row">
        <div className="edc-title-left">
          <h1 className="edc-title">
            {firstEventData?.attributes?.group_name}
          </h1>
          <div className="edc-title-meta">
            {subTypeName && <TypeBadge name={subTypeName} />}
            {firstEventData?.attributes?.venue_name && (
              <span className="edc-venue-tag">
                {selVenue && (
                  <>
                    <i className="bi bi-geo-alt" />
                    {selVenue?.label || " "}
                  </>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="edc-title-right">
          <ShareButton shareText="Check out this awesome event" />
        </div>
      </div>

      {/* ── 3. Registration form ── */}
      <div className="edc-form-card">
        {loading ? (
          <div className="edc-loading">Loading available options…</div>
        ) : venueOptions.length === 0 ? (
          <div className="edc-empty">
            No events are currently open for registration.
          </div>
        ) : (
          <>
            {/* ── Venue + secondary filter in a row ── */}
            <div className="edc-selector-row">
              {/* Venue */}
              <div className="edc-selector-col">
                <label className="edc-label">
                  Venue <span className="edc-req">*</span>
                </label>
                <Select
                  options={venueOptions}
                  value={selVenue}
                  onChange={(v) => setSelVenue(v)}
                  placeholder="Select a venue…"
                  isClearable
                  styles={selStyles()}
                  classNamePrefix="edc-sel"
                />
              </div>

              {/* School dropdown (only if multiple schools exist) */}
              {showSchoolDropdown && (
                <div className="edc-selector-col">
                  <label className="edc-label">
                    Host School <span className="edc-req">*</span>
                  </label>
                  <Select
                    options={schools.map((s) => ({
                      value: s.id,
                      label: s.name,
                    }))}
                    value={
                      selSchool
                        ? { value: selSchool.id, label: selSchool.name }
                        : null
                    }
                    onChange={(v) => {
                      const s = schools.find((sc) => sc.id === v?.value);
                      setSelSchool(s || null);
                      setSelGender(null);
                      setSelAgeGrade(null);
                      setSelEvents([]);
                    }}
                    placeholder="Select a school…"
                    isClearable
                    styles={selStyles()}
                  />
                </div>
              )}
            </div>

            {/* Auto-selected school row */}
            {selVenue && schools.length === 1 && selSchool && (
              <div className="edc-auto-row">
                <Check size={15} className="edc-auto-icon" />
                <span>
                  Host School: <strong>{selSchool.name}</strong>
                </span>
                <span className="edc-auto-tag">Auto-selected</span>
              </div>
            )}

            {/* ── Gender standalone section (above Age, since the available
                   age/grade options depend on the selected gender) ── */}
            {showGenderStep && (
              <div className="edc-age-section">
                <label className="edc-label">
                  <User size={14} style={{ verticalAlign: "middle" }} /> Gender
                </label>
                <div className="edc-chips edc-chips--wrap">
                  {genders.map((g) => (
                    <button
                      key={g}
                      type="button"
                      className={`edc-chip${selGender === g ? " edc-chip--on" : ""}`}
                      onClick={() => {
                        setSelGender(g);
                        setSelAgeGrade(null);
                        setSelEvents([]);
                      }}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Age / Grade standalone section ── */}
            {showBatchSection && showAgeGradeStep && (
              <div className="edc-age-section">
                <label className="edc-label">
                  {ageGradeOptions[0]?.type === "grade" ? (
                    <>
                      <GraduationCap
                        size={14}
                        style={{ verticalAlign: "middle" }}
                      />{" "}
                      Grade
                    </>
                  ) : (
                    <>
                      <Cake size={14} style={{ verticalAlign: "middle" }} /> Age
                      Group
                    </>
                  )}
                </label>
                <div className="edc-chips edc-chips--wrap">
                  {ageGradeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`edc-chip${selAgeGrade?.value === opt.value ? " edc-chip--on" : ""}`}
                      onClick={() => {
                        setSelAgeGrade(opt);
                        setSelWeight(null);
                        setSelEvents([]);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showBatchSection && weightResolved && weightOptions.length > 0 && (
              <div className="edc-age-section">
                <label className="edc-label">Weight</label>

                <div className="edc-chips edc-chips--wrap">
                  {weightOptions.map((opt: any) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`edc-chip${
                        selWeight?.value === opt.value ? " edc-chip--on" : ""
                      }`}
                      onClick={() => {
                        setSelWeight(opt);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Select Batch section (appears after venue selected) ── */}
            {showBatchCards && (
              <div className="edc-batch-section">
                <h3 className="edc-batch-title">Select Event</h3>

                {/* Batch cards */}
                {showBatchCards && (
                  <div className="edc-batches">
                    {matchingEvents.map((ev, idx) => {
                      const a = ev.attributes;
                      const date =
                        a?.event_start_date ||
                        a?.fees_id?.data?.attributes?.activity_start_date;
                      const endDate =
                        a?.event_end_date ||
                        a?.fees_id?.data?.attributes?.activity_end_date;
                      const title =
                        a?.title && a.title !== a.group_name
                          ? a.title
                          : `B${idx + 1}`;
                      const pt = a?.participation_type;
                      const fee = a?.event_fee;
                      const isSelected = selEvents.some((e) => e.id === ev.id);
                      return (
                        <button
                          key={ev.id}
                          type="button"
                          className={`edc-batch${isSelected ? " edc-batch--on" : ""}`}
                          onClick={() =>
                            setSelEvents((prev) =>
                              prev.some((e) => e.id === ev.id)
                                ? prev.filter((e) => e.id !== ev.id)
                                : [...prev, ev],
                            )
                          }
                        >
                          {isSelected && (
                            <span className="edc-batch__check">
                              <Check size={14} />
                            </span>
                          )}
                          <span className="edc-batch__name">{title}</span>
                          <span className="edc-batch__meta">
                            {date && (
                              <span>
                                {fmtDate(date)}
                                {endDate && endDate !== date
                                  ? ` – ${fmtDate(endDate)}`
                                  : ""}
                              </span>
                            )}
                            {pt && (
                              <span className="edc-batch__pill">{pt}</span>
                            )}
                            {fee && (
                              <span className="edc-batch__price">
                                {fmtPrice(Math.round(fee))}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Register CTA */}
                {showBatchCards && (
                  <div className="edc-cta-row">
                    <button
                      type="button"
                      className="edc-btn-register"
                      disabled={!eventResolved}
                      onClick={handleRegister}
                    >
                      {isRegistering ? "Please wait..." : "Register Now →"}
                    </button>
                    {!eventResolved && (
                      <span className="edc-cta-hint">
                        Select one or more events above to continue
                      </span>
                    )}
                    {eventResolved && selEvents.length > 1 && (
                      <span className="edc-cta-hint">
                        {selEvents.length} events selected
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── 4. About ── */}
      {firstEventData?.attributes?.summary && (
        <div className="viva-detail-card mt-4">
          <h4 className="viva-detail-card__title">About</h4>
          <p className="viva-detail-card__text">
            {firstEventData.attributes.summary}
          </p>
        </div>
      )}

      {/* ── 5. Rules ── */}
      {!isPrivate && firstEventData?.attributes?.rules_regulation && (
        <div className="viva-detail-card mt-3 mb-5">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h5 className="viva-detail-card__title" style={{ margin: 0 }}>
              Rules &amp; Regulations
            </h5>
            {(() => {
              const rulesContent =
                firstEventData.attributes.rules_regulation || "";
              const pdfUrl = rulesContent.match(
                /href="([^"]*\.pdf[^"]*)"/i,
              )?.[1];
              if (!pdfUrl) return null;
              return (
                <a
                  href={pdfUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="viva-detail-card__download"
                >
                  <i className="fa fa-download" /> Download
                </a>
              );
            })()}
          </div>
          <div
            className="viva-detail-card__text"
            style={{ overflow: "hidden" }}
            dangerouslySetInnerHTML={{
              __html: (() => {
                let c = firstEventData.attributes.rules_regulation || "";
                c = c.replace(
                  /<a[^>]*href="[^"]*\.pdf[^"]*"[^>]*>[\s\S]*?<\/a>/gi,
                  "",
                );
                return c || "<div></div>";
              })(),
            }}
          />
        </div>
      )}
    </div>
  );
}

/* ─── TypeBadge ─────────────────────────────────────────────────────────────── */

const TypeBadge = ({ name }: { name: string }) => {
  const styles: Record<string, CSSProperties> = {
    Competition: {
      border: "1px solid #212121",
      color: "#310000",
      background: "#fff",
    },
    Workshop: {
      border: "1px solid #212121",
      color: "#310000",
      background:
        "linear-gradient(0deg, rgba(33,33,33,.12), rgba(33,33,33,.12)), #FFF",
    },
    "Premier Competition": {
      border: "1px solid #3F41D1",
      color: "#3F41D1",
      background:
        "linear-gradient(0deg, rgba(63,65,209,.12), rgba(63,65,209,.12)), #FFF",
    },
  };
  const style: CSSProperties = styles[name] || {
    border: "1px solid #212121",
    color: "#310000",
    background: "#fff",
  };
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 14px",
        borderRadius: "34px",
        fontSize: "0.85rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {name}
    </div>
  );
};

/* ─── react-select styles ────────────────────────────────────────────────────── */
const selStyles = () => ({
  control: (p: any, s: any) => ({
    ...p,
    minHeight: "52px",
    fontSize: "1rem",
    borderColor: s.isFocused ? "#3F41D1" : "#e6e8f2",
    boxShadow: s.isFocused ? "0 0 0 2px rgba(63,65,209,.18)" : "none",
    borderRadius: "10px",
    "&:hover": { borderColor: "#3F41D1" },
  }),
  placeholder: (p: any) => ({ ...p, fontSize: "1rem", color: "#9ca3af" }),
  singleValue: (p: any) => ({
    ...p,
    fontSize: "1rem",
    color: "#16182d",
    fontWeight: 500,
  }),
  option: (p: any, s: any) => ({
    ...p,
    fontSize: "0.95rem",
    backgroundColor: s.isSelected
      ? "#3F41D1"
      : s.isFocused
        ? "#f3f4ff"
        : "white",
    color: s.isSelected ? "white" : "#16182d",
  }),
  menu: (p: any) => ({
    ...p,
    borderRadius: "10px",
    boxShadow: "0 8px 30px rgba(22,24,45,.12)",
  }),
});
